import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Breadcrumb, Button, Form, Header, Icon, Menu, Popup, Segment } from 'semantic-ui-react';
import { editCard, getCard } from '../../Api/api';
import { CardAndFields, CardForm, CardInfo, FieldInfo, FieldValues } from '../../Api/types';
import { formatDateSec2String } from '../../helpers';
import styles from './CardEditPage.module.css';

const inputTypeFn = function (type: string) {
    const MAP = {
        typeText: 'text',
        typeTextPerson: 'text',
        typeNumber: 'text',
        typePhone: 'tel',
        typeDateTime: 'text',
        typeDate: 'text',
        typeTime: 'text'
    };

    return MAP[type as keyof typeof MAP] || 'text';
};

const inputPlaceholderFn = function (type: string) {
    const MAP = {
        typePhone: '+79990001234',
        typeDateTime: 'ДД.ММ.ГГГГ ЧЧ:ММ',
        typeDate: 'ДД.ММ.ГГГГ',
        typeTime: 'ЧЧ:ММ'
    };

    return MAP[type as keyof typeof MAP] || '';
};

const compareUserFieldsByPosition = function (a: FieldInfo, b: FieldInfo) {
    return Number(a.position) - Number(b.position);
};

function CardEditPage() {
    const { cardId } = useParams();

    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('');
    const [number, setNumber] = useState('');
    const [createdDate, setCreatedDate] = useState(0);
    const [name, setName] = useState('');
    const [balance, setBalance] = useState(0);
    const [holder, setHolder] = useState('');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [birthdate, setBirthdate] = useState('');
    const [isBirthdateFixed, setIsBirthdateFixed] = useState(false);
    const [userFields, setUserFields] = useState([] as Array<FieldInfo>);
    const [userFieldsValues, setUserFieldsValues] = useState({} as FieldValues);

    useEffect(() => {
        getCard(cardId)
            .then((result: CardAndFields) => {
                const card: CardInfo = result.card;
                setStatus(card.status);
                setNumber(card.number);
                setCreatedDate(card.created_date);
                setName(card.name);
                setBalance(card.balance);
                card.form.holder && setHolder(card.form.holder);
                card.form.address && setAddress(card.form.address);
                card.form.phone && setPhone(card.form.phone);
                card.form.email && setEmail(card.form.email);
                card.form.birthdate && setBirthdate(card.form.birthdate);

                if (card.form.birthdate) {
                    setIsBirthdateFixed(true);
                }

                const fields = result.fields;
                const userFields = fields.filter((item) => item.account_id);
                userFields.sort(compareUserFieldsByPosition);
                setUserFields(userFields);

                const fieldsValues: FieldValues = {};

                userFields.forEach((field) => {
                    fieldsValues[field.uuid] = card.form[field.uuid];
                })

                setUserFieldsValues(prev => ({
                    ...prev,
                    ...fieldsValues,
                }));
            })
            .catch((err) => {
                toast.error(err.message);
            })
            .finally(() => {
                setLoading(false);
            })
    }, [cardId]);

    const save = function () {
        setLoading(true);
        const form: CardForm = {
            holder,
            email,
            birthdate,
            address,
        };

        for (const key in userFieldsValues) {
            form[key] = userFieldsValues[key];
        }

        editCard(cardId, { card: { form } })
            .then(() => {
                toast.success('Карта ' + number + ' сохранена.');
                navigate("/");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            <Menu borderless>
                <Menu.Item header>
                    <Breadcrumb className={styles['breadcrumb']}>
                        <Link to="/"><Breadcrumb.Section>Главная</Breadcrumb.Section></Link>
                        {number && <Breadcrumb.Divider icon='right chevron' />}
                        {number && <Breadcrumb.Section active>Карта № {number}</Breadcrumb.Section>}
                    </Breadcrumb>
                </Menu.Item>
                <Menu.Menu position='right'>
                    <Menu.Item>
                        <Button.Group fluid>
                            <Button color="teal" onClick={save}>Записать</Button>
                            <Button onClick={() => { navigate("/"); }}>Отмена</Button>
                        </Button.Group>
                    </Menu.Item>
                </Menu.Menu>
            </Menu>

            <Segment className={styles['card-edit-segment']}>

                <Form className={loading ? 'loading' : ''} onSubmit={save}>
                    <Header as='h3' dividing>
                        Карта
                    </Header>

                    {status === 'active' && <Icon color='green' name='lock open' />}
                    {status === 'blocked' && <Icon color='red' name='lock' />}
                    <span>{status === 'active' ? 'активна' : 'заблокирована'}</span>

                    <Form.Group widths='equal'>
                        <Form.Input readOnly fluid label='Номер карты' value={number} />
                        <Form.Input readOnly fluid label='Дата создания' value={formatDateSec2String(createdDate)} />
                    </Form.Group>

                    <Form.Group widths='equal'>
                        <Form.Input readOnly fluid label='Наименование торговой точки' value={name} />
                        <Form.Input readOnly fluid label='Баланс, бнс.' value={balance} />
                    </Form.Group>

                    <Form.Input fluid label='Владелец' value={holder} onChange={e => setHolder(e.target.value)} />
                    <Form.Input fluid label='Адрес' value={address} onChange={e => setAddress(e.target.value)} />

                    <Form.Group widths='equal'>
                        <Form.Input readOnly fluid label='Номер телефона' placeholder='+7-999-000-00-00' value={phone} />
                        <Form.Input fluid label='Электронная почта' placeholder='client@dclubs.ru' value={email} onChange={e => setEmail(e.target.value)} />
                        <Popup content='Дату рождения можно указать только один раз без возможности последующего изменения.'
                            trigger={<Form.Input readOnly={isBirthdateFixed} type="date" fluid label='Дата рождения' value={birthdate}
                                onChange={e => setBirthdate(e.target.value)} />} />
                    </Form.Group>

                    {userFields.length && Object.keys(userFieldsValues).length &&
                        userFields.map((field) => <Form.Input required={field.required === '1'} fluid type={inputTypeFn(field.type)} key={field.uuid}
                            label={field.name} placeholder={inputPlaceholderFn(field.type)} value={userFieldsValues[field.uuid as keyof FieldValues]}
                            onChange={e => setUserFieldsValues(prev => ({ ...prev, [field.uuid]: e.target.value, }))} />)}

                    <Button.Group fluid>
                        <Button onClick={() => { navigate("/"); }}>Отмена</Button>
                        <Button color="teal" type='submit'>Записать</Button>
                    </Button.Group>
                </Form>
            </Segment>
        </>
    );
}

export default CardEditPage;